import { create, el } from "@dhx/ts-common/dom";
import { ItemType } from "@dhx/ts-toolbar";
import { IRibbonElement, IBlock } from "./types";
import { createFactory, Navbar, IState } from "@dhx/ts-navbar";
import { IHandlers } from "@dhx/ts-common/types";
import { extend } from "@dhx/ts-common/core";
import { locate, locateNode } from "@dhx/ts-common/html";
import { tooltip, Position } from "@dhx/ts-message";

export class Ribbon extends Navbar<IRibbonElement> {
	private _listeners: IHandlers;

	constructor(element?: string | HTMLElement, config?) {
		super(element, extend({
			navigationType: "click"
		}, config));
		this._listeners = {
			input: (e: Event) => {
				const id = locate(e);
				this.data.update(id, {value: (e.target as HTMLInputElement).value});
			},
			tooltip: (e: MouseEvent) => {
				const elem = locateNode(e);
				if (!elem) {
					return;
				}
				const id = elem.getAttribute("dhx_id");
				const item = this.data.getItem(id);
				if (item.tooltip) {
					tooltip(item.tooltip, {
						node: elem as HTMLElement,
						position: Position.bottom
					});
				}
			}
		};

		this._currentRoot = null;
		const render = () => this._draw();
		this.mount(element, create({ render }));
	}
	getState(): IState {
		const state = {};
		this.data.eachChild(this.data.getRoot(), item => {
			if ((item as any).twoState && !item.group) {
				state[item.id] = (item as any).active;
			} else if (item.type === ItemType.input || item.type === ItemType.selectButton) {
				state[item.id] = item.value;
			}
		}, true);
		for (const key in this._groups) {
			if (this._groups[key].active) {
				state[key] = this._groups[key].active;
			}
		}
		return state;
	}
	setState(state: IState): void {
		for (const key in state) {
			if (this._groups && this._groups[key]) {
				if (this._groups[key].active) {
					this.data.update(this._groups[key].active, {active: false});
					this._groups[key].active = state[key];
					this.data.update(state[key], {active: true});
				}
			} else {
				const item = this.data.getItem(key);
				if (item.type === ItemType.input || item.type === ItemType.selectButton) {
					this.data.update(key, {value: state[key]});
				} else {
					this.data.update(key, {active: state[key]});
				}
			}
		}
	}
	protected _getFactory() {
		return createFactory({
			widget: this,
			defaultType: ItemType.navItem,
			allowedTypes: [
				ItemType.navItem,
				ItemType.button,
				ItemType.customHTMLButton,
				ItemType.customHTML,
				ItemType.imageButton,
				ItemType.input,
				ItemType.selectButton,
				ItemType.separator,
				ItemType.spacer,
				ItemType.title
			],
			widgetName: "ribbon"
		});
	}
	protected _getMode(item, root): "right" | "bottom" {
		return item.id === root ? "bottom" : "right";
	}
	protected _close(e: MouseEvent): void {
		this._activePosition = null;
		this._currentRoot = null;
		super._close(e);
	}
	protected _draw() {
		return el("ul.dhx_ribbon.dhx_widget", {
			dhx_widget_id: this._uid,
			class: this.config.css ? this.config.css : "",
			tabindex: 0,
			onclick: this._handlers.onclick,
			onmousedown: this._handlers.onmousedown,
			oninput: this._listeners.input,
			onmouseover: this._listeners.tooltip,
			_hooks: {
				didInsert: node => {
					node.el.addEventListener("keyup", (e: KeyboardEvent) => {
						if (e.which !== 9) {
							return;
						}
						const elem = locateNode(document.activeElement);
						if (!elem) {
							return;
						}

						const id = elem.getAttribute("dhx_id");
						const item = this.data.getItem(id);
						if (item.tooltip) {
							tooltip(item.tooltip, {
								node: elem as HTMLElement,
								position: Position.bottom,
								force: true
							});
						}
					}, true);
				}
			}
		}, [
			el("li", {
				class: "dhx_ribbon-block dhx_ribbon-block--root"
			}, [
				el("ul.dhx_ribbon-content.dhx_ribbon-content--full-width", this.data.map(block => block.type === "block" ? this._drawBlock(block, true) : this._factory(block), this.data.getRoot(), false)),
			])
		]);
	}
	protected _setRoot(id: string) {
		const parentId = this.data.getParent(id);
		if (this.data.getItem(parentId).type === ItemType.block) {
			this._currentRoot = id;
		}
	}
	private _drawBlock(block: IBlock, isFirst?: boolean) {
		if (!block || block.hidden) {
			return null;
		}
		const classNames = "dhx_ribbon-block" +
			(" dhx_ribbon-block" + (block.direction === "col" ? "--col" : "--row")) +
			(block.title ? " dhx_ribbon-block--title" : "") +
			(block.css ? " " + block.css : "") +
			(isFirst ? " dhx_ribbon-block--indented": "");

		const childs = this.data.map(child => {
			if (child.type === ItemType.block) {
				return this._drawBlock(child);
			}
			if (child.type !== ItemType.separator && child.type !== ItemType.spacer) {
				return this._factory(child);
			}
			return null;
		}, block.id, false);

		return el("li", {
			class: classNames
		}, [
			el("ul.dhx_ribbon-content", childs),
			block.title ? el("span.dhx_ribbon-content-label-wrapper", [el("span.dhx_ribbon-content-label", block.title)]) : null
		]);
	}
}